import { useQuery } from '@tanstack/react-query';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import type { InvitationValidation } from '@/types/roc/models/InvitationValidation';
import type { RecipientReferralView } from '@/types/roc/models/RecipientReferralView';
import { ResponseError } from '@/types/roc/runtime';

import { getPublicReferralAccessQueryKey } from '../queryKeys';

export interface PublicReferralAccessData {
  invitation: InvitationValidation;
  referral: RecipientReferralView;
}

export function getPublicReferralAccessErrorMessage(
  error: unknown,
  fallback: string
) {
  if (error instanceof ResponseError) {
    if (error.response.status === 401 || error.response.status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
    if (error.response.status === 404) {
      return 'This referral could not be found.';
    }
  }

  return fallback;
}

export function usePublicReferralAccess(token: string | undefined) {
  return useQuery({
    queryKey: getPublicReferralAccessQueryKey(token),
    queryFn: async (): Promise<PublicReferralAccessData> => {
      if (!token) {
        throw new Error('Missing referral access token.');
      }

      const invitationClient = createPublicRocClient();
      const invitation = await invitationClient.getInvitation({ token });

      const referralClient = createPublicRocClient(token);
      const referral = await referralClient.getRecipientReferralView({
        referralId: invitation.referral_id
      });

      return { invitation, referral };
    },
    enabled: Boolean(token),
    retry: false
  });
}
